import { useEffect, useState, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService'; // Replace api.js
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import Select from 'react-select';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';

function Bids() {
  const { isAdmin } = useContext(AuthContext);
  const [statusFilter, setStatusFilter] = useState('');
  const [completionFilter, setCompletionFilter] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isRequestBidsModalOpen, setIsRequestBidsModalOpen] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [requestBids, setRequestBids] = useState([]);
  const [form, setForm] = useState({ status: '' });

  // Memoize the query string
  const queryString = useMemo(() => {
    const query = new URLSearchParams();
    if (statusFilter) query.set('status', statusFilter);
    if (completionFilter) query.set('completion', completionFilter);
    return query.toString();
  }, [statusFilter, completionFilter]);

  const bidsEndpoint = `/waste/bids${queryString ? `?${queryString}` : ''}`;
  const { data: bids, loading, error, fetchData: fetchBids } = useApi(bidsEndpoint);
  const { data: userDetails, loading: userLoading, error: userError, fetchData: fetchUserDetails } = useApi(null);
  const { data: requestBidsData, loading: bidsLoading, error: bidsError, fetchData: fetchRequestBids } = useApi(null);

  useEffect(() => {
    if (isAdmin()) {
      fetchBids();
    }
  }, [bidsEndpoint, fetchBids, isAdmin]);

  useEffect(() => {
    if (requestBidsData) {
      setRequestBids(requestBidsData);
    }
  }, [requestBidsData]);

  useEffect(() => {
    if (userDetails) {
      setSelectedUser(userDetails);
    }
  }, [userDetails]);

  useEffect(() => {
    if (bids) {
      console.log('Bids data:', bids);
    }
  }, [bids]);

  if (!isAdmin()) {
    toast.error('Access denied. Admins only.');
    return <Navigate to="/login" />;
  }

  const handleStatusChange = (selected) => {
    setStatusFilter(selected ? selected.value : '');
  };

  const handleCompletionChange = (selected) => {
    setCompletionFilter(selected ? selected.value : '');
  };

  const handleEdit = (bid) => {
    setEditingBid(bid);
    setForm({ status: bid.status });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (requestId, bidId) => {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      try {
        await apiService.delete(`/waste/bid/${requestId}/${bidId}`);
        toast.success('Bid deleted successfully');
        fetchBids();
      } catch (err) {
        console.error('Delete bid error:', err);
        // apiService's interceptor handles toast for 401, 403, etc.
      }
    }
  };

  const handleViewUserDetails = async (userId, role) => {
    if (!userId) {
      toast.error('Invalid user ID');
      return;
    }
    const endpoint = role === 'farmer' ? `/waste/farmer-details/${userId}` : `/waste/collector-details/${userId}`;
    console.log('Calling fetchUserDetails with:', { endpoint, userId, role });
    try {
      await fetchUserDetails(endpoint);
      setIsDetailsModalOpen(true);
    } catch (err) {
      console.error('handleViewUserDetails error:', err);
      // apiService's interceptor handles toast
    }
  };

  const handleViewRequestBids = async (requestId) => {
    if (!requestId) {
      toast.error('Invalid request ID');
      return;
    }
    const endpoint = `/waste/bids/${requestId}`;
    console.log('Calling fetchRequestBids with:', { endpoint, requestId });
    try {
      await fetchRequestBids(endpoint);
      setIsRequestBidsModalOpen(true);
    } catch (err) {
      console.error('handleViewRequestBids error:', err);
      // apiService's interceptor handles toast
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.put(`/waste/bid/status/${editingBid.requestId}/${editingBid._id}`, {
        status: form.status,
      });
      toast.success('Bid status updated successfully');
      fetchBids();
      setIsEditModalOpen(false);
      setEditingBid(null);
      setForm({ status: '' });
    } catch (err) {
      console.error('Update bid error:', err);
      // apiService's interceptor handles toast
    }
  };

  const columns = [
    {
      key: 'farmer',
      label: 'Farmer',
      render: (b) => (
        <button
          onClick={() => handleViewUserDetails(b.farmerId, 'farmer')}
          className="text-blue-600 hover:underline"
        >
          {b.farmer}
        </button>
      ),
    },
    {
      key: 'collector',
      label: 'Collector',
      render: (b) => (
        <button
          onClick={() => handleViewUserDetails(b.collectorId, 'waste_collector')}
          className="text-blue-600 hover:underline"
        >
          {b.collector}
        </button>
      ),
    },
    { key: 'amount', label: 'Bid Amount ($)' },
    { key: 'note', label: 'Note' },
    { key: 'status', label: 'Status' },
    { key: 'requestStatus', label: 'Request Status' },
    { key: 'quantity', label: 'Quantity (kg)' },
    { key: 'type', label: 'Type' },
    {
      key: 'createdAt',
      label: 'Date',
      render: (b) => new Date(b.createdAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }),
    },
    {
      key: 'viewBids',
      label: 'Bids',
      render: (b) => (
        <button
          onClick={() => handleViewRequestBids(b.requestId)}
          className="text-green-500 hover:text-green-600"
          title="View Bids"
        >
          <FaEye />
        </button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (b) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(b)}
            className="text-yellow-500 hover:text-yellow-600"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDelete(b.requestId, b._id)}
            className="text-red-500 hover:text-red-600"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'completed', label: 'Completed' },
  ];

  const completionOptions = [
    { value: '', label: 'All Completions' },
    { value: 'pending', label: 'Pending' },
    { value: 'bidding', label: 'Bidding' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'completed', label: 'Completed' },
    { value: 'canceled', label: 'Canceled' },
  ];

  const requestBidsColumns = [
    {
      key: 'collector',
      label: 'Collector',
      render: (b) => `${b.collectorId?.firstName || 'N/A'} ${b.collectorId?.lastName || ''}`,
    },
    { key: 'amount', label: 'Bid Amount ($)' },
    { key: 'note', label: 'Note' },
    { key: 'status', label: 'Status' },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (b) => new Date(b.createdAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }),
    },
  ];

  return (
    <div className="p-5  dark:bg-gray-900 min-h-screen">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Bid Management</h2>
      <div className="flex flex-col sm:flex-row sm:space-x-4 mb-6">
        <div className="w-full sm:w-1/2 mb-4 sm:mb-0">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Filter by Bid Status
          </label>
          <Select
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === statusFilter) || null}
            onChange={handleStatusChange}
            placeholder="Select status"
            isClearable
            className="mt-1"
            classNamePrefix="react-select"
          />
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Filter by Request Completion
          </label>
          <Select
            options={completionOptions}
            value={completionOptions.find((opt) => opt.value === completionFilter) || null}
            onChange={handleCompletionChange}
            placeholder="Select completion"
            isClearable
            className="mt-1"
            classNamePrefix="react-select"
          />
        </div>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          Loading...
        </div>
      ) : error ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">
          Error: {error}
        </div>
      ) : !bids || bids.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No bids found.
        </div>
      ) : (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={columns} data={bids} />
        </div>
      )}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingBid(null);
          setForm({ status: '' });
        }}
        title="Update Bid Status"
        className="max-w-full lg:max-w-lg mx-4"
      >
        <div className="p-5">
          <form onSubmit={handleSubmit}>
            <FormInput
              label="Status"
              name="status"
              type="select"
              value={form.status}
              onChange={(e) => setForm({ status: e.target.value })}
              required
              className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select status</option>
              {statusOptions
                .filter((opt) => opt.value !== '')
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </FormInput>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingBid(null);
                  setForm({ status: '' });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={loading}
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedUser(null);
        }}
        title={`${selectedUser?.role === 'farmer' ? 'Farmer' : 'Waste Collector'} Details`}
        className="max-w-full lg:max-w-lg mx-4"
      >
        <div className="p-5">
          {userLoading ? (
            <p className="text-gray-600 dark:text-gray-300">Loading...</p>
          ) : userError ? (
            <p className="text-red-600 dark:text-red-400">Error: {userError}</p>
          ) : selectedUser ? (
            <div className="space-y-2 text-gray-900 dark:text-gray-100">
              <p><strong>Name:</strong> {selectedUser.firstName} {selectedUser.lastName}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Phone:</strong> {selectedUser.phone}</p>
              <p><strong>Address:</strong> {selectedUser.address}</p>
              <p><strong>Street:</strong> {selectedUser.street}</p>
              <p><strong>Postal Code:</strong> {selectedUser.postalCode}</p>
              <p><strong>Role:</strong> {selectedUser.role}</p>
              <p><strong>Active:</strong> {selectedUser.active ? 'Yes' : 'No'}</p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">No details available</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => {
                setIsDetailsModalOpen(false);
                setSelectedUser(null);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isRequestBidsModalOpen}
        onClose={() => {
          setIsRequestBidsModalOpen(false);
          setRequestBids([]);
        }}
        title="Bids for Waste Request"
        className="max-w-full lg:max-w-2xl mx-4"
      >
        <div className="p-5">
          {bidsLoading ? (
            <p className="text-gray-600 dark:text-gray-300">Loading...</p>
          ) : bidsError ? (
            <p className="text-red-600 dark:text-red-400">Error: {bidsError}</p>
          ) : requestBids.length > 0 ? (
            <Table columns={requestBidsColumns} data={requestBids} />
          ) : (
            <p className="text-gray-600 dark:text-gray-300">No bids found for this request.</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => {
                setIsRequestBidsModalOpen(false);
                setRequestBids([]);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Bids;